'use client';
import React, { useState } from 'react';
import Link from 'next/link';

const NAV_LINKS = [
{ label: 'Services', href: '#services' },
{ label: 'Work', href: '#portfolio' },
{ label: 'Pricing', href: '#pricing' },
{ label: 'About', href: '#about' },
{ label: 'Contact', href: '#contact' }];


const SERVICES = [
{
  icon: '🌐',
  title: 'Custom Website Design',
  desc: 'Pixel-perfect, brand-aligned websites built from scratch. We design with your audience in mind — not templates.',
  tag: 'Most Popular'
},
{
  icon: '⚡',
  title: 'Web App Development',
  desc: 'Full-stack web applications using React, Next.js, and Node.js. Scalable, fast, and built to grow with your business.',
  tag: ''
},
{
  icon: '🛒',
  title: 'E-Commerce Solutions',
  desc: 'Shopify, WooCommerce, or custom storefronts. We build online stores that convert visitors into paying customers.',
  tag: ''
},
{
  icon: '📱',
  title: 'Mobile-First Development',
  desc: 'Responsive websites that look and perform flawlessly on every device — from phones to 4K monitors.',
  tag: ''
},
{
  icon: '🔍',
  title: 'SEO & Performance',
  desc: 'Technical SEO, Core Web Vitals optimization, and speed tuning. We build sites Google loves to rank.',
  tag: ''
},
{
  icon: '🔧',
  title: 'Maintenance & Support',
  desc: 'Ongoing updates, security patches, and performance monitoring. We keep your site running at peak condition.',
  tag: ''
}];


const PORTFOLIO = [
{
  title: 'LuxeCart — Fashion E-Commerce',
  category: 'E-Commerce',
  result: '+340% conversion rate in 3 months',
  color: '#1a1a2e',
  accent: '#C8102E',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1963287ca-1772219977287.png",
  alt: 'Fashion e-commerce website showing clothing products on a clean white background'
},
{
  title: 'NovaMed — Healthcare Portal',
  category: 'Web App',
  result: '12,000+ patient bookings/month',
  color: '#0a2540',
  accent: '#0078ff',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1dfe52c02-1772883271786.png",
  alt: 'Healthcare portal dashboard with appointment scheduling interface'
},
{
  title: 'GreenLeaf — Organic Brand',
  category: 'Brand + Website',
  result: '5× organic traffic growth',
  color: '#0d2b1a',
  accent: '#22c55e',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_14598cd78-1768148847505.png",
  alt: 'Organic food brand website with green vegetables and clean design'
},
{
  title: 'FinEdge — Fintech Dashboard',
  category: 'Web App',
  result: 'Raised $2M seed after launch',
  color: '#1a1a2e',
  accent: '#f59e0b',
  img: "https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b",
  alt: 'Financial dashboard with charts and analytics data visualization'
}];


const PRICING = [
{
  name: 'Starter',
  price: '$799',
  period: 'one-time',
  desc: 'Perfect for small businesses and personal brands getting online.',
  features: [
  '5-page responsive website',
  'Mobile-optimized design',
  'Contact form integration',
  'Basic SEO setup',
  '1 month free support',
  'Google Analytics setup'],

  cta: 'Get Started',
  highlight: false
},
{
  name: 'Growth',
  price: '$1,999',
  period: 'one-time',
  desc: 'For growing businesses that need a powerful online presence.',
  features: [
  'Up to 15 pages',
  'Custom UI/UX design',
  'CMS integration (WordPress/Sanity)',
  'Advanced SEO optimization',
  'Speed & Core Web Vitals tuning',
  '3 months free support',
  'Social media integration',
  'Blog setup'],

  cta: 'Most Popular',
  highlight: true
},
{
  name: 'Enterprise',
  price: 'Custom',
  period: 'quote',
  desc: 'Full-scale web apps, e-commerce platforms, and complex systems.',
  features: [
  'Unlimited pages',
  'Custom web application',
  'E-commerce / payment integration',
  'API development & integrations',
  'Dedicated project manager',
  '12 months priority support',
  'Performance SLA guarantee',
  'Security audit included'],

  cta: 'Request Quote',
  highlight: false
}];


const TESTIMONIALS = [
{
  name: 'Sarah Mitchell',
  role: 'Founder, LuxeCart',
  quote: 'IjPrinter Connect Inc\'s team completely transformed our online store. Our revenue tripled in the first quarter after launch. The attention to detail is unmatched.',
  avatar: 'SM',
  rating: 5
},
{
  name: 'Dr. James Okafor',
  role: 'CEO, NovaMed Health',
  quote: 'We needed a complex patient portal built fast. IjPrinter Connect Inc delivered on time, on budget, and the quality exceeded our expectations. Highly recommend.',
  avatar: 'JO',
  rating: 5
},
{
  name: 'Priya Sharma',
  role: 'Marketing Director, GreenLeaf',
  quote: 'Not just developers — they\'re strategic partners. They understood our brand deeply and built a site that truly represents who we are.',
  avatar: 'PS',
  rating: 5
}];


const FAQS = [
{
  q: 'How long does it take to build a website?',
  a: 'A standard 5–10 page website typically takes 3–5 weeks from kickoff to launch. Complex web applications or e-commerce platforms may take 8–16 weeks depending on scope and features.'
},
{
  q: 'Do you offer ongoing maintenance after launch?',
  a: 'Yes. All packages include a free support period after launch. We also offer monthly maintenance plans covering updates, security patches, backups, and performance monitoring.'
},
{
  q: 'What technologies do you use?',
  a: 'We primarily build with React, Next.js, TypeScript, and Tailwind CSS for the frontend. For backends, we use Node.js, PostgreSQL, and various headless CMS platforms. We choose the right stack for each project.'
},
{
  q: 'Will my website be mobile-friendly?',
  a: 'Absolutely. Every website we build is fully responsive and tested across all major devices and browsers. Mobile-first design is a core part of our development process.'
},
{
  q: 'Do you provide hosting and domain services?',
  a: 'We can help you set up hosting on platforms like Vercel, Netlify, or AWS. We guide you through domain registration and DNS configuration, though hosting costs are separate from our development fees.'
},
{
  q: 'What is your payment structure?',
  a: 'We typically work on a 50% upfront, 50% on delivery basis for fixed-price projects. For larger enterprise projects, we offer milestone-based payment schedules.'
}];


export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({ name: '', email: '', project: '', budget: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", backgroundColor: '#fff', color: '#1a1a2e' }}>
      {/* ── HEADER ── */}
      <header
        className="sticky top-0 z-50 w-full"
        style={{ backgroundColor: 'rgba(255,255,255,0.97)', borderBottom: '2px solid #C8102E', backdropFilter: 'blur(8px)' }}>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link href="/" className="flex items-center gap-2">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: '#C8102E', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                I
              </div>
              <span
                className="font-bold text-xl"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1a1a2e' }}>
                IjPrinter Connect Inc
              </span>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              {NAV_LINKS.map((l) =>
              <a
                key={l.label}
                href={l.href}
                className="text-sm font-medium text-gray-600 hover:text-red-700 transition-colors">
                  {l.label}
                </a>
              )}
              <a
                href="#contact"
                className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90"
                style={{ backgroundColor: '#C8102E' }}>
                Start a Project
              </a>
            </nav>
            <a
              href="#contact"
              className="md:hidden px-4 py-2 rounded-lg text-sm font-semibold text-white"
              style={{ backgroundColor: '#C8102E' }}>
              Hire Us
            </a>
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #2d1b1b 100%)',
          minHeight: '92vh',
          display: 'flex',
          alignItems: 'center'
        }}>
        
        <div
          className="absolute top-20 right-10 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #C8102E 0%, transparent 70%)', filter: 'blur(40px)' }} />
        
        <div
          className="absolute bottom-20 left-10 w-64 h-64 rounded-full opacity-8"
          style={{ background: 'radial-gradient(circle, #4da6ff 0%, transparent 70%)', filter: 'blur(60px)' }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold mb-6"
              style={{ backgroundColor: 'rgba(200,16,46,0.15)', color: '#ff6b6b', border: '1px solid rgba(200,16,46,0.3)' }}>
              <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse inline-block"></span>
              Available for new projects — 2 spots open
            </div>
            <h1
              className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              We Build Websites
              <br />
              <span style={{ color: '#C8102E' }}>That Actually</span>
              <br />
              Drive Results.
            </h1>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-10 max-w-2xl">
              IjPrinter Connect Inc is a professional printer support and connectivity service. We help you set up, connect, and troubleshoot your printer with fast, modern, and reliable solutions for homes and businesses worldwide.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#contact"
                className="px-8 py-4 rounded-xl text-base font-bold text-white transition-all hover:scale-105"
                style={{ backgroundColor: '#C8102E', boxShadow: '0 0 30px rgba(200,16,46,0.4)' }}>
                Start Your Project →
              </a>
              <a
                href="#portfolio"
                className="px-8 py-4 rounded-xl text-base font-semibold text-white transition-all hover:bg-white hover:text-gray-900"
                style={{ border: '1.5px solid rgba(255,255,255,0.25)' }}>
                View Our Work
              </a>
            </div>
            <div className="flex flex-wrap gap-8 mt-12">
              {[
              { num: '150+', label: 'Projects Delivered' },
              { num: '98%', label: 'Client Satisfaction' },
              { num: '7 yrs', label: 'Industry Experience' },
              { num: '40+', label: 'Countries Served' }].
              map((s) =>
              <div key={s.label}>
                  <p className="text-3xl font-bold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.num}</p>
                  <p className="text-sm text-gray-400 mt-0.5">{s.label}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="services" className="py-24 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#f8f9fc' }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-14">
            <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#C8102E' }}>What We Do</p>
            <h2 className="text-4xl md:text-5xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1a1a2e' }}>
              Services Built for
              <br />
              <span style={{ color: '#C8102E' }}>Real Business Growth</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) =>
            <div
              key={s.title}
              className="relative p-7 rounded-2xl transition-all hover:-translate-y-1 hover:shadow-xl"
              style={{
                backgroundColor: i === 0 ? '#1a1a2e' : '#fff',
                border: i === 0 ? 'none' : '1px solid #e8eaf0'
              }}>
                {s.tag &&
              <span
                className="absolute top-4 right-4 text-xs font-bold px-2.5 py-1 rounded-full"
                style={{ backgroundColor: '#C8102E', color: '#fff' }}>
                    {s.tag}
                  </span>
              }
                <div className="text-3xl mb-4">{s.icon}</div>
                <h3
                className="text-lg font-bold mb-2"
                style={{ color: i === 0 ? '#fff' : '#1a1a2e', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {s.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: i === 0 ? '#a0aec0' : '#64748b' }}>
                  {s.desc}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── PORTFOLIO ── */}
      <section id="portfolio" className="py-24 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-14">
            <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#C8102E' }}>Our Work</p>
            <h2 className="text-4xl md:text-5xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1a1a2e' }}>
              Projects That Speak
              <br />
              <span style={{ color: '#C8102E' }}>for Themselves</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PORTFOLIO.map((p) =>
            <div
              key={p.title}
              className="group relative overflow-hidden rounded-2xl"
              style={{ backgroundColor: p.color, minHeight: '280px' }}>
                <img
                src={p.img}
                alt={p.alt}
                className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity" />
                <div className="relative p-8 flex flex-col justify-between h-full" style={{ minHeight: '280px' }}>
                  <div>
                    <span
                    className="text-xs font-bold px-3 py-1 rounded-full"
                    style={{ backgroundColor: p.accent, color: '#fff' }}>
                      {p.category}
                    </span>
                    <h3
                    className="text-2xl font-bold text-white mt-4"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      {p.title}
                    </h3>
                  </div>
                  <div
                  className="flex items-center gap-2 mt-6 text-sm font-semibold"
                  style={{ color: p.accent }}>
                    <span>📈</span>
                    <span>{p.result}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#f8f9fc' }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-14 text-center">
            <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#C8102E' }}>Transparent Pricing</p>
            <h2 className="text-4xl md:text-5xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1a1a2e' }}>
              No Hidden Fees.
              <br />
              <span style={{ color: '#C8102E' }}>Just Great Websites.</span>
            </h2>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto text-base">
              All prices are in USD. Final pricing may vary based on project complexity. A detailed quote is provided before any work begins.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {PRICING.map((plan) =>
            <div
              key={plan.name}
              className="relative rounded-2xl p-8 flex flex-col"
              style={{
                backgroundColor: plan.highlight ? '#1a1a2e' : '#fff',
                border: plan.highlight ? '2px solid #C8102E' : '1px solid #e8eaf0',
                boxShadow: plan.highlight ? '0 0 40px rgba(200,16,46,0.2)' : 'none'
              }}>
                {plan.highlight &&
              <div
                className="absolute -top-3.5 left-1/2 -translate-x-1/2 text-xs font-bold px-4 py-1 rounded-full text-white"
                style={{ backgroundColor: '#C8102E' }}>
                    Most Popular
                  </div>
              }
                <div>
                  <p
                  className="text-sm font-semibold uppercase tracking-wider mb-2"
                  style={{ color: plan.highlight ? '#ff6b6b' : '#C8102E' }}>
                    {plan.name}
                  </p>
                  <div className="flex items-end gap-1 mb-1">
                    <span
                    className="text-4xl font-bold"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: plan.highlight ? '#fff' : '#1a1a2e' }}>
                      {plan.price}
                    </span>
                    {plan.period !== 'quote' &&
                  <span className="text-sm pb-1" style={{ color: plan.highlight ? '#a0aec0' : '#64748b' }}>
                        /{plan.period}
                      </span>
                  }
                  </div>
                  <p className="text-sm mb-6" style={{ color: plan.highlight ? '#a0aec0' : '#64748b' }}>
                    {plan.desc}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((f) =>
                  <li key={f} className="flex items-start gap-2.5 text-sm" style={{ color: plan.highlight ? '#e2e8f0' : '#374151' }}>
                        <span style={{ color: '#C8102E', flexShrink: 0 }}>✓</span>
                        {f}
                      </li>
                  )}
                  </ul>
                </div>
                <a
                href="#contact"
                className="mt-auto block text-center py-3.5 rounded-xl font-semibold text-sm transition-all hover:opacity-90"
                style={{
                  backgroundColor: plan.highlight ? '#C8102E' : 'transparent',
                  color: plan.highlight ? '#fff' : '#C8102E',
                  border: plan.highlight ? 'none' : '1.5px solid #C8102E'
                }}>
                  {plan.cta}
                </a>
              </div>
            )}
          </div>
          <p className="text-center text-xs text-gray-400 mt-6">
            * All prices are estimates. Final cost depends on project scope. No payment is collected until a formal written agreement is signed.
          </p>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" className="py-24 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#C8102E' }}>About IjPrinter Connect Inc</p>
              <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1a1a2e' }}>
                A Team That Treats
                <br />
                <span style={{ color: '#C8102E' }}>Your Business as Ours</span>
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5 text-base">
                IjPrinter Connect Inc is a trusted printer support and connectivity service founded in 2017. We specialize in helping homes and businesses set up, connect, and maintain their printers with fast and reliable solutions.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8 text-base">
                We don't just write code — we solve business problems. Every project starts with understanding your goals, your users, and your market. Then we build something that actually works.
              </p>
              <div className="grid grid-cols-2 gap-5">
                {[
                { icon: '🎯', title: 'Goal-Driven', desc: 'Every design decision is tied to your business objectives.' },
                { icon: '🔒', title: 'Transparent', desc: 'Clear contracts, honest timelines, no surprise fees.' },
                { icon: '⚡', title: 'Fast Delivery', desc: 'Agile process with weekly updates and quick turnarounds.' },
                { icon: '🤝', title: 'Long-Term Partner', desc: 'We build lasting relationships, not one-off projects.' }].
                map((v) =>
                <div key={v.title} className="p-5 rounded-xl" style={{ backgroundColor: '#f8f9fc', border: '1px solid #e8eaf0' }}>
                    <div className="text-2xl mb-2">{v.icon}</div>
                    <h4 className="font-bold text-sm mb-1" style={{ color: '#1a1a2e' }}>{v.title}</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">{v.desc}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="relative">
              <div
                className="rounded-2xl overflow-hidden"
                style={{ aspectRatio: '4/3', backgroundColor: '#1a1a2e' }}>
                <img
                  src="https://images.unsplash.com/photo-1637979911089-bf0d73f0b9c5"
                  alt="Web development team collaborating around a table with laptops and design mockups"
                  className="w-full h-full object-cover opacity-80" />
              </div>
              <div
                className="absolute -bottom-5 -left-5 p-5 rounded-xl shadow-xl"
                style={{ backgroundColor: '#C8102E' }}>
                <p className="text-3xl font-bold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>7+</p>
                <p className="text-xs text-red-200 mt-0.5">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#1a1a2e' }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-14 text-center">
            <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#ff6b6b' }}>Client Stories</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              What Our Clients Say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) =>
            <div
              key={t.name}
              className="p-7 rounded-2xl"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) =>
                <span key={i} className="text-yellow-400 text-sm">★</span>
                )}
                </div>
                <p className="text-gray-300 text-sm leading-relaxed mb-6">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white"
                  style={{ backgroundColor: '#C8102E' }}>
                    {t.avatar}
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">{t.name}</p>
                    <p className="text-gray-500 text-xs">{t.role}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#f8f9fc' }}>
        <div className="max-w-3xl mx-auto">
          <div className="mb-14 text-center">
            <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#C8102E' }}>FAQ</p>
            <h2 className="text-4xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1a1a2e' }}>
              Common Questions
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) =>
            <div
              key={i}
              className="rounded-xl overflow-hidden"
              style={{ border: '1px solid #e8eaf0', backgroundColor: '#fff' }}>
                <button
                className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-semibold text-sm" style={{ color: '#1a1a2e' }}>{faq.q}</span>
                  <span
                  className="text-lg font-bold flex-shrink-0 transition-transform"
                  style={{ color: '#C8102E', transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0deg)' }}>
                    +
                  </span>
                </button>
                {openFaq === i &&
              <div className="px-6 pb-5">
                    <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                  </div>
              }
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" className="py-24 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#C8102E' }}>Get In Touch</p>
              <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1a1a2e' }}>
                Ready to Build
                <br />
                <span style={{ color: '#C8102E' }}>Something Great?</span>
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Tell us about your project. We'll review your requirements and get back to you within 24 hours with a free consultation.
              </p>
              <div className="space-y-4">
                {[
                { icon: '📧', label: 'Email', value: 'info@ijprinter-connect.online' },
                { icon: '📞', label: 'Phone', value: '+1 (301) 568-8546' },
                { icon: '🕐', label: 'Response Time', value: 'Within 24 hours' }].
                map((c) =>
                <div key={c.label} className="flex items-center gap-3">
                    <span className="text-xl">{c.icon}</span>
                    <div>
                      <p className="text-xs text-gray-400">{c.label}</p>
                      <p className="text-sm font-semibold" style={{ color: '#1a1a2e' }}>{c.value}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div>
              {submitted ?
              <div
                className="h-full flex flex-col items-center justify-center text-center p-10 rounded-2xl"
                style={{ backgroundColor: '#f8f9fc', border: '1px solid #e8eaf0' }}>
                  <div className="text-5xl mb-4">🎉</div>
                  <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1a1a2e' }}>
                    Message Received!
                  </h3>
                  <p className="text-gray-500 text-sm">We'll review your project and get back to you within 24 hours.</p>
                </div> :
              <form
                onSubmit={handleSubmit}
                className="p-8 rounded-2xl space-y-5"
                style={{ backgroundColor: '#f8f9fc', border: '1px solid #e8eaf0' }}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Your Name *</label>
                      <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Smith"
                      className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all"
                      style={{ border: '1.5px solid #e8eaf0', backgroundColor: '#fff', color: '#1a1a2e' }} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email Address *</label>
                      <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@company.com"
                      className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all"
                      style={{ border: '1.5px solid #e8eaf0', backgroundColor: '#fff', color: '#1a1a2e' }} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Project Description *</label>
                    <textarea
                    required
                    rows={4}
                    value={formData.project}
                    onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                    placeholder="Tell us about your project, goals, and timeline..."
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none resize-none"
                    style={{ border: '1.5px solid #e8eaf0', backgroundColor: '#fff', color: '#1a1a2e' }} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Estimated Budget</label>
                    <select
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none"
                    style={{ border: '1.5px solid #e8eaf0', backgroundColor: '#fff', color: '#1a1a2e' }}>
                      <option value="">Select a range</option>
                      <option value="under-1k">Under $1,000</option>
                      <option value="1k-3k">$1,000 – $3,000</option>
                      <option value="3k-10k">$3,000 – $10,000</option>
                      <option value="10k-plus">$10,000+</option>
                    </select>
                  </div>
                  <button
                  type="submit"
                  className="w-full py-4 rounded-xl font-bold text-white text-sm transition-all hover:opacity-90 hover:shadow-lg"
                  style={{ backgroundColor: '#C8102E' }}>
                    Send Project Brief →
                  </button>
                  <p className="text-xs text-gray-400 text-center">
                    By submitting this form, you agree to our{' '}
                    <a href="#privacy" className="underline hover:text-gray-600">Privacy Policy</a>.
                    No payment is required at this stage.
                  </p>
                </form>
              }
            </div>
          </div>
        </div>
      </section>

      {/* ── POLICIES ── */}
      <section id="privacy" className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#f8f9fc', borderTop: '1px solid #e8eaf0' }}>
        <div className="max-w-4xl mx-auto space-y-10">
          <div>
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1a1a2e' }}>
              Privacy Policy
            </h2>
            <div className="text-sm text-gray-600 space-y-3 leading-relaxed">
              <p><strong>Last updated: January 2025</strong></p>
              <p>IjPrinter Connect Inc ("IjPrinter Connect", "we", "us") respects your privacy. This policy explains how we collect, use, and protect your personal information when you use our website or services.</p>
              <p><strong>Information We Collect:</strong> We collect information you provide directly (name, email, project details via contact forms) and automatically collected data (IP address, browser type, pages visited) via analytics tools.</p>
              <p><strong>How We Use Your Information:</strong> To respond to inquiries, provide services, send project updates, and improve our website. We do not sell your personal data to third parties.</p>
              <p><strong>Cookies:</strong> We use cookies for analytics (Google Analytics) and to improve user experience. You may disable cookies in your browser settings.</p>
              <p><strong>Data Retention:</strong> We retain contact form submissions for up to 2 years. You may request deletion at any time by emailing info@ijprinter-connect.online.</p>
              <p><strong>Your Rights:</strong> You have the right to access, correct, or delete your personal data. Contact us at info@ijprinter-connect.online for any privacy-related requests.</p>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1a1a2e' }}>
              Terms of Service
            </h2>
            <div className="text-sm text-gray-600 space-y-3 leading-relaxed">
              <p><strong>Last updated: January 2025</strong></p>
              <p>By using this website or engaging IjPrinter Connect Inc for services, you agree to the following terms:</p>
              <p><strong>Services:</strong> IjPrinter Connect Inc provides printer support, setup, connectivity, and related services. All project scopes, timelines, and costs are agreed upon in writing before work begins.</p>
              <p><strong>Payment:</strong> Standard payment terms are 50% upfront and 50% upon project completion. Enterprise projects may use milestone-based payment schedules as agreed in the project contract.</p>
              <p><strong>Intellectual Property:</strong> Upon full payment, clients receive full ownership of the final deliverables. IjPrinter Connect Inc retains the right to display completed work in its portfolio unless otherwise agreed in writing.</p>
              <p><strong>Revisions:</strong> Each package includes a defined number of revision rounds. Additional revisions beyond the agreed scope will be billed at our standard hourly rate.</p>
              <p><strong>Limitation of Liability:</strong> IjPrinter Connect Inc is not liable for indirect, incidental, or consequential damages arising from the use of our services. Our total liability shall not exceed the amount paid for the specific project.</p>
              <p><strong>Governing Law:</strong> These terms are governed by applicable law. Disputes shall be resolved through good-faith negotiation before any legal proceedings.</p>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1a1a2e' }}>
              Disclaimer
            </h2>
            <div className="text-sm text-gray-600 space-y-3 leading-relaxed">
              <p><strong>Results Disclaimer:</strong> Case studies and client results shown on this website (e.g., "+340% conversion rate") are real results from specific clients and are not guarantees of future performance. Individual results vary based on industry, market conditions, budget, and other factors.</p>
              <p><strong>Pricing Disclaimer:</strong> All prices listed are starting estimates. Final project costs depend on scope, complexity, and specific requirements. A detailed written quote is provided before any work or payment is requested.</p>
              <p><strong>No Guarantee:</strong> While we strive to deliver high-quality work on time, we cannot guarantee specific business outcomes, search engine rankings, or revenue results from websites we build.</p>
              <p><strong>Third-Party Services:</strong> Our websites may integrate third-party tools (Google Analytics, payment processors, CMS platforms). We are not responsible for the policies or performance of these third-party services.</p>
              <p><strong>Accuracy of Information:</strong> We make every effort to keep information on this website accurate and up to date. However, we do not warrant that all content is error-free or complete.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ backgroundColor: '#1a1a2e', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                  style={{ backgroundColor: '#C8102E', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  I
                </div>
                <span className="font-bold text-xl text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  IjPrinter Connect Inc
                </span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
                IjPrinter Connect Inc — professional printer support and connectivity service helping homes and businesses set up and maintain their printers since 2017.
              </p>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">Services</h4>
              <ul className="space-y-2">
                {['Website Design', 'Web App Development', 'E-Commerce', 'SEO & Performance', 'Maintenance'].map((s) =>
                <li key={s}>
                    <a href="#services" className="text-sm text-gray-500 hover:text-white transition-colors">{s}</a>
                  </li>
                )}
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">Company</h4>
              <ul className="space-y-2">
                {[
                { label: 'About Us', href: '#about' },
                { label: 'Our Work', href: '#portfolio' },
                { label: 'Pricing', href: '#pricing' },
                { label: 'Contact', href: '#contact' },
                { label: 'Privacy Policy', href: '#privacy' },
                { label: 'Terms of Service', href: '#privacy' },
                { label: 'Disclaimer', href: '#privacy' }].
                map((l) =>
                <li key={l.label}>
                    <a href={l.href} className="text-sm text-gray-500 hover:text-white transition-colors">{l.label}</a>
                  </li>
                )}
              </ul>
            </div>
          </div>
          <div
            className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-3"
            style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="text-xs text-gray-600">
              © 2025 IjPrinter Connect Inc. All rights reserved. Results shown are from real clients and are not guarantees of future performance.
            </p>
            <div className="flex gap-4">
              <a href="#privacy" className="text-xs text-gray-600 hover:text-gray-400 transition-colors">Privacy</a>
              <a href="#privacy" className="text-xs text-gray-600 hover:text-gray-400 transition-colors">Terms</a>
              <a href="#privacy" className="text-xs text-gray-600 hover:text-gray-400 transition-colors">Disclaimer</a>
            </div>
          </div>
        </div>
      </footer>
    </div>);

}